import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Image, message, Modal, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";
import PageTab from "@/components/i-pageTab/i-pageTab";
import PageTable from "@/components/i-pageTable/i-pageTable";
import EnumText from "@/components/i-enumText/i-enumText";
import {
  deleteUserIdentity,
  selectCmsUserIdentityPage,
  userIdentityExport,
} from "@/api/operation";
import { dictInfoAsync } from "@/stores/dictInfo";
import {
  downloadFile,
  getTimeFormat,
  setIcons,
} from "@/utils/common";
import { userLevelOptions } from "@/utils/enums";

const initialFormData: SelectCmsUserIdentityPageParams = {
  endTime: "",
  mobile: "",
  nickName: "",
  startTime: "",
  userBusinessId: "",
  userLevel: "",
  ypageNum: 1,
  ypageSize: 10,
};

const UserIdentity = (): React.ReactElement => {
  const dispatch = useDispatch();
  const providersLevel = useSelector(
    (state: { dictInfo: { state: { providers_level?: unknown[] } } }) =>
      state.dictInfo.state.providers_level
  );
  const [formData, setFormData] =
    useState<SelectCmsUserIdentityPageParams>(initialFormData);
  const [pageData, setPageData] = useState<SelectCmsUserIdentityPageRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const getPageData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await selectCmsUserIdentityPage(formData);
      setPageData(data.rows);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [formData]);

  useEffect(() => {
    void getPageData();
  }, [getPageData]);

  useEffect(() => {
    if (!providersLevel) {
      void dispatch(dictInfoAsync("providers_level") as never);
    }
  }, [dispatch, providersLevel]);

  const handleSearch = useCallback(
    (values: SelectCmsUserIdentityPageParams): void => {
      setFormData((current) => ({
        ...current,
        ...values,
        ypageNum: 1,
      }));
    },
    []
  );

  const handleReset = useCallback((): void => {
    setFormData(initialFormData);
  }, []);

  const handlePageChange = useCallback(
    (current: number, pageSize: number): void => {
      setFormData((values) => ({
        ...values,
        ypageNum: current,
        ypageSize: pageSize,
      }));
    },
    []
  );

  const handleDelete = useCallback(
    (id: number): void => {
      Modal.confirm({
        title: "系统提示",
        content: "是否确认删除该实名认证？",
        cancelText: "取消",
        okText: "确定",
        onOk: async (): Promise<void> => {
          await deleteUserIdentity(id);
          message.success("删除成功");
          await getPageData();
        },
      });
    },
    [getPageData]
  );

  const handleExport = useCallback(async (): Promise<void> => {
    setExportLoading(true);
    try {
      const data = await userIdentityExport(formData);
      downloadFile(
        data,
        `实名认证列表${getTimeFormat(new Date(), "yyyy-MM-dd HH:mm:ss")}`
      );
    } finally {
      setExportLoading(false);
    }
  }, [formData]);

  const formTab = useMemo(
    () => [
      {
        label: "用户业务ID",
        name: "userBusinessId",
        type: "input",
      },
      {
        label: "用户昵称",
        name: "nickName",
        type: "input",
      },
      {
        label: "手机号",
        name: "mobile",
        type: "input",
      },
      {
        label: "用户等级",
        name: "userLevel",
        type: "select",
        options: providersLevel,
      },
      {
        label: "认证时间",
        name: "rangePicker",
        type: "rangePicker",
        width: "380px",
      },
    ],
    [providersLevel]
  );

  const footer = useMemo(
    () => [
      {
        title: "导出",
        icon: setIcons("DownloadOutlined"),
        onClick: handleExport,
        loading: exportLoading,
      },
    ],
    [exportLoading, handleExport]
  );

  const columns = useMemo<ColumnsType<SelectCmsUserIdentityPageRow>>(
    () => [
      {
        title: "用户业务ID",
        dataIndex: "userBusinessId",
        key: "userBusinessId",
      },
      {
        title: "用户昵称",
        dataIndex: "nickName",
        key: "nickName",
      },
      {
        title: "手机号",
        dataIndex: "mobile",
        key: "mobile",
      },
      {
        title: "真实姓名",
        dataIndex: "realName",
        key: "realName",
      },
      {
        title: "身份证号",
        dataIndex: "identityNumber",
        key: "identityNumber",
      },
      {
        title: "联系电话",
        dataIndex: "contactMobile",
        key: "contactMobile",
      },
      {
        title: "用户等级",
        dataIndex: "userLevel",
        key: "userLevel",
        render: (value: string): React.ReactNode => (
          <EnumText options={userLevelOptions} value={value} />
        ),
      },
      {
        title: "身份证人像面",
        dataIndex: "portraitPic",
        key: "portraitPic",
        render: (url: string): React.ReactNode =>
          url ? <Image width={80} src={url} /> : "-",
      },
      {
        title: "身份证国徽面",
        dataIndex: "nationalEmblemPic",
        key: "nationalEmblemPic",
        render: (url: string): React.ReactNode =>
          url ? <Image width={80} src={url} /> : "-",
      },
      {
        title: "认证时间",
        dataIndex: "createTime",
        key: "createTime",
      },
      {
        title: "操作",
        dataIndex: "id",
        key: "id",
        fixed: "right",
        render: (id: number): React.ReactNode => (
          <Space>
            <Button
              danger
              size="small"
              type="text"
              onClick={() => handleDelete(id)}
            >
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [handleDelete]
  );

  return (
    <div>
      <PageTab
        defaultTimer={[]}
        footer={footer}
        formTab={formTab}
        reset={handleReset}
        search={handleSearch}
      />
      <PageTable
        columns={columns}
        data={pageData}
        loading={loading}
        pageChange={handlePageChange}
        pagination={{
          current: formData.ypageNum,
          pageSize: formData.ypageSize,
          total,
        }}
        rowKey="id"
      />
    </div>
  );
};

export default UserIdentity;
